import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useUser } from "./userContext";
import axiosInstance from "../../services/api/axiosInstance";
import { initNotifications, registerNotificationWorker, requestNotificationPermission, setupMessageListener, unregisterFCMToken } from "../config/firebase/pushNotifications";
import { notificationBelongsTo, notificationIdentity, readNotificationList, safeNotificationUrl } from "../../shared/notifications/notificationData";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useUser();
  const identity = notificationIdentity(user);
  // Remounting also drops in-flight results, read operations and toast deduplication for the old account.
  return <NotificationSession key={identity} user={user}>{children}</NotificationSession>;
}

function NotificationSession({ children, user }) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [state, setState] = useState({ notifications: [], unreadCount: 0 });
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState("");
  const [pushStatus, setPushStatus] = useState("");
  const [pushBusy, setPushBusy] = useState(false);
  const [marking, setMarking] = useState(false);
  const active = useRef(true);
  const flight = useRef(null);
  const rerun = useRef(false);
  const seen = useRef(new Set());
  const toastIds = useRef(new Set());
  const userRef = useRef(user);
  userRef.current = user;
  const readLock = useRef(false);

  const refresh = useCallback(() => {
    if (!active.current || !userRef.current) return Promise.resolve();
    if (flight.current) { rerun.current = true; return flight.current; }
    const run = async () => {
      do {
        rerun.current = false;
        try {
          const response = await axiosInstance.get("/notifications/list", { params: { limit: 20 } });
          const next = readNotificationList(response.data, userRef.current);
          if (active.current) { setState(next); setError(""); }
        } catch {
          if (active.current) setError("Notifications could not be refreshed. Your existing list is preserved. Please retry.");
        }
      } while (active.current && rerun.current);
      if (active.current) setLoading(false);
    };
    flight.current = run().finally(() => { flight.current = null; });
    return flight.current;
  }, []);

  useEffect(() => {
    active.current = true;
    if (!userRef.current) { setLoading(false); return undefined; }
    let cancelled = false;
    let unsubscribe = () => {};
    const sessionToastIds = toastIds.current;
    const reportPushError = () => {
      if (active.current) setPushStatus("Browser push could not connect. The bell still works. Retry enabling browser notifications.");
    };
    const syncPush = () => initNotifications().catch(reportPushError);
    const reset = () => {
      active.current = false;
      cancelled = true;
      unsubscribe();
      seen.current.clear();
      sessionToastIds.forEach((id) => toast.dismiss(id));
      sessionToastIds.clear();
      setState({ notifications: [], unreadCount: 0 });
      setError("");
      setLoading(false);
    };
    const logout = () => { reset(); unregisterFCMToken(); };
    const resume = () => {
      if (document.hidden || !active.current) return;
      refresh();
      syncPush();
    };
    const workerMessage = (event) => {
      if (!active.current || event.data?.type !== "notification-click"
        || !notificationBelongsTo(event.data.data, userRef.current)) return;
      refresh();
      navigateRef.current(safeNotificationUrl(event.data.data.actionUrl));
    };
    refresh();
    registerNotificationWorker().catch(reportPushError);
    syncPush();
    setupMessageListener((payload) => {
      const data = payload.data || {};
      if (!active.current || !notificationBelongsTo(data, userRef.current)
        || (data.event !== "NOTIFICATION_CREATED" && data.refreshNotifications !== "true")) return;
      refresh();
      const id = data.notificationId || data.messageId;
      if (!id || seen.current.has(id)) return;
      seen.current.add(id);
      if (seen.current.size > 200) seen.current.delete(seen.current.values().next().value);
      const toastId = toast.info("You have a new Scholaro notification.", {
        onClick: () => navigateRef.current(safeNotificationUrl(data.actionUrl)),
      });
      sessionToastIds.add(toastId);
    }).then((cleanup) => { if (cancelled) cleanup(); else unsubscribe = cleanup; }).catch(reportPushError);
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("notifications:reset", reset);
    window.addEventListener("auth:logout", logout);
    navigator.serviceWorker?.addEventListener("message", workerMessage);
    return () => {
      cancelled = true;
      active.current = false;
      unsubscribe();
      sessionToastIds.forEach((id) => toast.dismiss(id));
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("notifications:reset", reset);
      window.removeEventListener("auth:logout", logout);
      navigator.serviceWorker?.removeEventListener("message", workerMessage);
      unregisterFCMToken();
    };
  }, [refresh]);

  const enablePush = async () => {
    if (pushBusy || !active.current) return;
    setPushBusy(true);
    try {
      const token = await requestNotificationPermission();
      if (active.current) setPushStatus(token ? "Browser notifications enabled." : "Browser push is unavailable here. The bell still works.");
    } catch (requestError) {
      if (active.current) setPushStatus(requestError.message || "Browser notifications could not be enabled. Please retry.");
    } finally {
      if (active.current) setPushBusy(false);
    }
  };
  const markRead = async (ids) => {
    if (readLock.current || !active.current) return false;
    const available = new Map(state.notifications.map((item) => [String(item.id), item]));
    const uniqueIds = [...new Set(ids.map(String))].map((id) => available.get(id)).filter((item) => item && !item.isRead).map((item) => item.id);
    if (!uniqueIds.length) return true;
    readLock.current = true;
    setMarking(true);
    try {
      const response = await axiosInstance.put("/notifications/mark-read", { notificationIds: uniqueIds });
      if (response.data?.success === false) throw new Error("Mark read was rejected.");
      if (!active.current) return false;
      await refresh();
      return true;
    } catch {
      if (active.current) setError("Notifications could not be marked as read. Nothing was changed locally. Please retry.");
      return false;
    } finally {
      readLock.current = false;
      if (active.current) setMarking(false);
    }
  };
  const dismiss = (id) => setState((current) => ({ ...current, notifications: current.notifications.filter((item) => item.id !== id) }));
  return <NotificationContext.Provider value={{ ...state, loading, error, refresh, markRead, marking, dismiss, enablePush, pushBusy, pushStatus }}>
    {children}
  </NotificationContext.Provider>;
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotifications requires NotificationProvider.");
  return context;
};
